import {Component, OnInit} from '@angular/core';
import {SessionStorageService} from '@services/session-storage.service';
import {storageKey} from '@constants/storage.constants';
import {AesService} from '@services/aes.service';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {BaseWebservices} from '@webservices/base.webservices';
import {Router} from '@angular/router';
import {AccountsWebservices} from '@webservices/accounts.webservices';
import {InstituteWebservices} from '@webservices/institute.webservices';

interface OtpState {
  sent: boolean;
  verified: boolean;
  loading: boolean;
  cooldown: number;
  timer?: any;
  errorMessage?: string;
}

@Component({
  selector: 'app-institute-verification',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule
  ],
  templateUrl: './institute-verification.component.html',
  styleUrl: './institute-verification.component.scss'
})
export class InstituteVerificationComponent implements OnInit {

  email!: string;

  verifyForm: FormGroup;

  emailState: OtpState = this.getInitialState();
  continueError?: string;

  constructor(
    private fb: FormBuilder,
    private storage: SessionStorageService,
    private aes: AesService,
    private account: AccountsWebservices,
    private institute: InstituteWebservices,
    private base: BaseWebservices,
    private router: Router,
  ) {
    this.verifyForm = this.fb.group({
      emailOtp: ['', [Validators.required, Validators.pattern(/^[0-9]{6}$/)]]
    });
  }

  ngOnInit(): void {
    const token: string | null = this.storage.get(storageKey.token);
    if (token) {
      const decrypt = this.aes.decrypt(token);
      this.email = decrypt.institute_email;
    } else {
      this.router.navigate(['not-found']);
    }
  }

  getInitialState(): OtpState {
    return {
      sent: false,
      verified: false,
      loading: false,
      cooldown: 0
    };
  }

  sendOtp() {
    if (this.emailState.cooldown > 0) return;

    this.emailState.loading = true;
    this.emailState.errorMessage = undefined;

    const payload = {
      otp_type: 'verify_institute_otp' as const,
      email: this.email ?? undefined,
    };

    this.base.sendOtp(payload).subscribe({
      next: (res) => {
        this.emailState.loading = false;
        if (!res.error) {
          this.emailState.sent = true;
          this.startCooldown(30);
        }
      },
      error: () => this.emailState.loading = false
    });
  }

  verifyOtp() {
    const control = this.verifyForm.get('emailOtp');

    if (control?.invalid) {
      control.markAllAsTouched();
      return;
    }

    this.emailState.errorMessage = undefined;

    const payload = {
      otp_type: 'verify_institute_otp' as const,
      email: this.email ?? undefined,
      received_otp: control?.value,
    };

    this.base.verifyOtp(payload).subscribe({
      next: (res) => {
        if (!res.error) {
          this.emailState.verified = true;
          this.emailState.sent = false;
        }
      },
      error: (err) => {
        const body = err?.error;
        if (body?.message?.display && body?.message?.message) {
          this.emailState.errorMessage = body.message.message;
        } else {
          this.emailState.errorMessage = 'Verification failed. Please try again.';
        }
      }
    });
  }

  resetField() {
    Object.assign(this.emailState, this.getInitialState());
    this.verifyForm.get('emailOtp')?.reset();
  }

  startCooldown(seconds: number) {
    this.emailState.cooldown = seconds;

    this.emailState.timer = setInterval(() => {
      this.emailState.cooldown--;

      if (this.emailState.cooldown <= 0) {
        clearInterval(this.emailState.timer);
      }
    }, 1000);
  }

  continue() {
    this.continueError = undefined;
    const payload = {
      email_otp: this.verifyForm.get('emailOtp')?.value,
      token: this.storage.get(storageKey.token) ?? undefined,
    };
    this.institute.verifyInstitute(payload).subscribe({
      next: (res) => {
        if (!res.error) {
          this.router.navigate(['/verification-complete']);
        }
      },
      error: (err) => {
        const body = err?.error;
        if (body?.message?.display && body?.message?.message) {
          this.continueError = body.message.message;
        } else {
          this.continueError = 'Something went wrong. Please try again.';
        }
      }
    });
  }
}
